import type { Paint } from "./styles";
import { paintsEqual as paintsContentEqual } from "./styles";

export type TextLayoutMode = "AUTO_WIDTH" | "AUTO_HEIGHT" | "FIXED_SIZE";
export type TextHorizontalAlign = "LEFT" | "CENTER" | "RIGHT" | "JUSTIFIED";
export type TextVerticalAlign = "TOP" | "CENTER" | "BOTTOM";
export type TextLineType = "NONE" | "ORDERED" | "UNORDERED";
export type TextLineHeight = "auto" | { unit: "px"; value: number } | { unit: "percent"; value: number };

export interface TypeStyle {
  fontFamily: string;
  fontWeight: number;
  fontSize: number;
  lineHeight: TextLineHeight;
  letterSpacing: number;
  horizontalAlign: TextHorizontalAlign;
  verticalAlign: TextVerticalAlign;
  fill?: Paint;
}

export interface RichTextModel {
  characters: string;
  style: TypeStyle;
  characterStyleOverrides: number[];
  styleOverrideTable: Record<number, Partial<TypeStyle>>;
  lineTypes: TextLineType[];
  lineIndentations: number[];
  textLayout: TextLayoutMode;
}

export function createDefaultTypeStyle(): TypeStyle {
  return {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: 12,
    lineHeight: "auto",
    letterSpacing: 0,
    horizontalAlign: "LEFT",
    verticalAlign: "TOP",
  };
}

export function resolvedLineHeight(style: Pick<TypeStyle, "fontSize" | "lineHeight">): number {
  const { lineHeight, fontSize } = style;
  if (lineHeight === "auto") {
    return fontSize * 1.25;
  }
  if (typeof lineHeight === "number") {
    return lineHeight;
  }
  if (lineHeight.unit === "px") {
    return lineHeight.value;
  }
  return fontSize * (lineHeight.value / 100);
}

export function isAutoLineHeight(lineHeight: TextLineHeight | number): boolean {
  return lineHeight === "auto";
}

export function lineHeightDisplayString(style: Pick<TypeStyle, "fontSize" | "lineHeight">): string {
  const { lineHeight } = style;
  if (lineHeight === "auto") {
    return "auto";
  }
  if (typeof lineHeight === "number") {
    return `${lineHeight}px`;
  }
  if (lineHeight.unit === "px") {
    return `${lineHeight.value}px`;
  }
  return `${lineHeight.value}%`;
}

export function parseLineHeightInput(input: string): TextLineHeight | undefined {
  const trimmed = input.trim().toLowerCase();
  if (trimmed === "auto") {
    return "auto";
  }

  const percentMatch = trimmed.match(/^(-?[\d.]+)\s*%$/);
  if (percentMatch) {
    const value = Number.parseFloat(percentMatch[1]!);
    if (Number.isFinite(value)) {
      return { unit: "percent", value };
    }
  }

  const pixelMatch = trimmed.match(/^(-?[\d.]+)\s*px$/);
  if (pixelMatch) {
    const value = Number.parseFloat(pixelMatch[1]!);
    if (Number.isFinite(value)) {
      return { unit: "px", value };
    }
  }

  const bareNumber = Number.parseFloat(trimmed);
  if (Number.isFinite(bareNumber) && /^-?[\d.]+$/.test(trimmed)) {
    return { unit: "px", value: bareNumber };
  }

  return undefined;
}

export function createDefaultRichText(characters = ""): RichTextModel {
  return {
    characters,
    style: createDefaultTypeStyle(),
    characterStyleOverrides: [],
    styleOverrideTable: {},
    lineTypes: lineTypesForText(characters),
    lineIndentations: lineTypesForText(characters).map(() => 0),
    textLayout: "AUTO_WIDTH",
  };
}

export function textWithCharacters(text: RichTextModel, characters: string): RichTextModel {
  return textWithEditedCharacters(text, characters);
}

export function textWithEditedCharacters(text: RichTextModel, characters: string): RichTextModel {
  const lineTypes = lineTypesForText(characters);
  const overrides = remapOverridesForTextEdit(text, characters);
  return {
    ...text,
    characters,
    characterStyleOverrides: overrides,
    lineTypes,
    lineIndentations: lineTypes.map((_, index) => text.lineIndentations[index] ?? 0),
  };
}

export function textWithDefaultStyle(text: RichTextModel, style: TypeStyle): RichTextModel {
  return {
    ...text,
    style,
  };
}

export function textWithRangeStyleOverride(text: RichTextModel, start: number, end: number, patch: Partial<TypeStyle>): RichTextModel {
  const safeStart = Math.max(0, Math.min(start, end, text.characters.length));
  const safeEnd = Math.max(safeStart, Math.min(Math.max(start, end), text.characters.length));
  if (safeStart === safeEnd) {
    return textWithDefaultStyle(text, { ...text.style, ...patch });
  }

  let nextText: RichTextModel = {
    ...text,
    characterStyleOverrides: [...text.characterStyleOverrides],
    styleOverrideTable: { ...text.styleOverrideTable },
  };

  for (let index = safeStart; index < safeEnd; index += 1) {
    const existingId = nextText.characterStyleOverrides[index] ?? 0;
    const existingStyle = existingId === 0 ? {} : nextText.styleOverrideTable[existingId] ?? {};
    const nextStyle = { ...existingStyle, ...patch };
    const nextId = findMatchingOverrideId(nextText.styleOverrideTable, nextStyle) ?? nextOverrideId(nextText.styleOverrideTable);
    nextText.styleOverrideTable[nextId] = nextStyle;
    nextText.characterStyleOverrides[index] = nextId;
  }

  return trimTrailingDefaultOverrides(nextText);
}

export function textWithUniformFontSize(text: RichTextModel, fontSize: number): RichTextModel {
  return textWithUniformStylePatch(text, { fontSize });
}

export function textWithUniformStylePatch(text: RichTextModel, patch: Partial<TypeStyle>): RichTextModel {
  if (text.characters.length === 0) {
    return { ...text, style: { ...text.style, ...patch } };
  }

  return {
    ...textWithRangeStyleOverride(text, 0, text.characters.length, patch),
    style: { ...text.style, ...patch },
  };
}

export type TextTypography = Omit<TypeStyle, "fill">;

const TYPOGRAPHY_STYLE_KEYS: Array<keyof TextTypography> = [
  "fontFamily",
  "fontWeight",
  "fontSize",
  "lineHeight",
  "letterSpacing",
  "horizontalAlign",
  "verticalAlign",
];

function stripTypographyFromPartialStyle(style: Partial<TypeStyle>): Partial<TypeStyle> {
  const next = { ...style };
  for (const key of TYPOGRAPHY_STYLE_KEYS) {
    delete next[key];
  }
  return next;
}

function paintsEqual(left: Paint | undefined, right: Paint | undefined): boolean {
  return paintsContentEqual(left, right);
}

export function textWithAppliedTypography(
  text: RichTextModel,
  typography: TextTypography,
  fill?: Paint,
): RichTextModel {
  const nextStyle: TypeStyle = {
    ...typography,
    fill: fill ?? text.style.fill,
  };

  if (text.characters.length === 0) {
    return { ...text, style: nextStyle };
  }

  const nextOverrideTable: Record<number, Partial<TypeStyle>> = {};
  const nextCharacterOverrides: number[] = [];

  for (let index = 0; index < text.characters.length; index += 1) {
    const resolved = resolvedTextStyleAt(text, index);
    const override = stripTypographyFromPartialStyle(
      resolved.fill && !paintsEqual(resolved.fill, nextStyle.fill) ? { fill: resolved.fill } : {},
    );

    if (Object.keys(override).length === 0) {
      nextCharacterOverrides.push(0);
      continue;
    }

    const nextId = findMatchingOverrideId(nextOverrideTable, override) ?? nextOverrideId(nextOverrideTable);
    nextOverrideTable[nextId] = override;
    nextCharacterOverrides.push(nextId);
  }

  return trimTrailingDefaultOverrides({
    ...text,
    style: nextStyle,
    characterStyleOverrides: nextCharacterOverrides,
    styleOverrideTable: nextOverrideTable,
  });
}

export function textWithBoldOverride(text: RichTextModel, start: number, end: number): RichTextModel {
  const safeStart = Math.max(0, Math.min(start, text.characters.length));
  const safeEnd = Math.max(safeStart, Math.min(end, text.characters.length));
  if (safeStart === safeEnd) {
    return textWithDefaultStyle(text, {
      ...text.style,
      fontWeight: text.style.fontWeight >= 700 ? 400 : 700,
    });
  }

  const allBold = Array.from({ length: safeEnd - safeStart }, (_, index) => resolvedTextStyleAt(text, safeStart + index))
    .every(style => style.fontWeight >= 700);
  return textWithRangeStyleOverride(text, safeStart, safeEnd, { fontWeight: allBold ? 400 : 700 });
}

function lineTypesForText(characters: string): TextLineType[] {
  return characters.split(/\n|\u2029/).map(() => "NONE");
}

function nextOverrideId(table: Record<number, Partial<TypeStyle>>): number {
  const ids = Object.keys(table).map(Number);
  return ids.length === 0 ? 1 : Math.max(...ids) + 1;
}

function findMatchingOverrideId(table: Record<number, Partial<TypeStyle>>, style: Partial<TypeStyle>): number | undefined {
  const serialized = JSON.stringify(style);
  const match = Object.entries(table).find(([, value]) => JSON.stringify(value) === serialized)?.[0];
  return match ? Number(match) : undefined;
}

export function resolvedTextStyleAt(text: RichTextModel, index: number): TypeStyle {
  const overrideId = text.characterStyleOverrides[index] ?? 0;
  return overrideId === 0
    ? text.style
    : { ...text.style, ...(text.styleOverrideTable[overrideId] ?? {}) };
}

export type Mixed<T> = T | "mixed";
export type TextInspectorRange = { start: number; end: number };

function resolvedStyleKey(style: TypeStyle): string {
  return JSON.stringify(style);
}

function styleRunBoundsAt(text: RichTextModel, index: number): TextInspectorRange {
  const key = resolvedStyleKey(resolvedTextStyleAt(text, index));
  let start = index;
  while (start > 0 && resolvedStyleKey(resolvedTextStyleAt(text, start - 1)) === key) {
    start -= 1;
  }

  let end = index + 1;
  while (end < text.characters.length && resolvedStyleKey(resolvedTextStyleAt(text, end)) === key) {
    end += 1;
  }

  return { start, end };
}

export function textInspectorRange(
  text: RichTextModel,
  selection?: { anchor: number; focus: number },
): TextInspectorRange {
  if (!selection) {
    return { start: 0, end: text.characters.length };
  }

  const { anchor, focus } = selection;
  if (anchor !== focus) {
    return { start: Math.min(anchor, focus), end: Math.max(anchor, focus) };
  }

  if (text.characters.length === 0) {
    return { start: 0, end: 0 };
  }

  const caret = Math.max(0, Math.min(focus, text.characters.length));
  const probe = caret === text.characters.length ? caret - 1 : caret;
  return styleRunBoundsAt(text, probe);
}

export const textStylePatchRange = textInspectorRange;

export function patchTextTypeStyleForInspector(
  text: RichTextModel,
  patch: Partial<TypeStyle>,
  textEditSelection?: { anchor: number; focus: number },
): RichTextModel {
  const patchRange = textStylePatchRange(text, textEditSelection);
  const nextText = patchRange.end > patchRange.start
    ? textWithRangeStyleOverride(text, patchRange.start, patchRange.end, patch)
    : { ...text, style: { ...text.style, ...patch } };
  const inTextEditWithRange = textEditSelection !== undefined && patchRange.end > patchRange.start;
  return !inTextEditWithRange && patchRange.end > patchRange.start
    ? { ...nextText, style: { ...nextText.style, ...patch } }
    : nextText;
}

function inspectTextStyleProperty<T>(
  text: RichTextModel,
  range: TextInspectorRange,
  read: (style: TypeStyle) => T,
  equals: (left: T, right: T) => boolean = (left, right) => Object.is(left, right),
): Mixed<T> {
  if (range.end <= range.start) {
    return read(text.style);
  }

  let value: T | undefined;
  for (let index = range.start; index < range.end; index += 1) {
    const next = read(resolvedTextStyleAt(text, index));
    if (value === undefined) {
      value = next;
      continue;
    }
    if (!equals(value, next)) {
      return "mixed";
    }
  }

  return value as T;
}

function lineHeightsEqual(left: TextLineHeight, right: TextLineHeight): boolean {
  if (left === right) {
    return true;
  }
  if (left === "auto" || right === "auto" || typeof left === "number" || typeof right === "number") {
    return false;
  }
  return left.unit === right.unit && left.value === right.value;
}

export interface TypeStyleInspectorSnapshot {
  fontFamily: Mixed<string>;
  fontWeight: Mixed<number>;
  fontSize: Mixed<number>;
  lineHeight: Mixed<TextLineHeight>;
  letterSpacing: Mixed<number>;
  horizontalAlign: Mixed<TextHorizontalAlign>;
  verticalAlign: Mixed<TextVerticalAlign>;
}

export function textTypeStyleInspector(text: RichTextModel, range: TextInspectorRange): TypeStyleInspectorSnapshot {
  return {
    fontFamily: inspectTextStyleProperty(text, range, style => style.fontFamily),
    fontWeight: inspectTextStyleProperty(text, range, style => style.fontWeight),
    fontSize: inspectTextStyleProperty(text, range, style => style.fontSize),
    lineHeight: inspectTextStyleProperty(text, range, style => style.lineHeight, lineHeightsEqual),
    letterSpacing: inspectTextStyleProperty(text, range, style => style.letterSpacing),
    horizontalAlign: inspectTextStyleProperty(text, range, style => style.horizontalAlign),
    verticalAlign: inspectTextStyleProperty(text, range, style => style.verticalAlign),
  };
}

export function textFontSizeInspectorValue(text: RichTextModel, range?: TextInspectorRange): Mixed<number> {
  return textTypeStyleInspector(text, range ?? { start: 0, end: text.characters.length }).fontSize;
}

export function textFillInspectorValue(
  text: RichTextModel,
  range: TextInspectorRange,
  fallbackFill?: Paint,
): Mixed<Paint | undefined> {
  return inspectTextStyleProperty(
    text,
    range,
    style => style.fill ?? fallbackFill,
    (left, right) => JSON.stringify(left) === JSON.stringify(right),
  );
}

function trimTrailingDefaultOverrides(text: RichTextModel): RichTextModel {
  const overrides = [...text.characterStyleOverrides];
  while (overrides.length > 0 && (overrides.at(-1) === 0 || overrides.at(-1) === undefined)) {
    overrides.pop();
  }
  return { ...text, characterStyleOverrides: overrides };
}

function remapOverridesForTextEdit(text: RichTextModel, nextCharacters: string): number[] {
  const previous = text.characters;
  let prefixLength = 0;
  while (
    prefixLength < previous.length &&
    prefixLength < nextCharacters.length &&
    previous[prefixLength] === nextCharacters[prefixLength]
  ) {
    prefixLength += 1;
  }

  let suffixLength = 0;
  while (
    suffixLength < previous.length - prefixLength &&
    suffixLength < nextCharacters.length - prefixLength &&
    previous[previous.length - 1 - suffixLength] === nextCharacters[nextCharacters.length - 1 - suffixLength]
  ) {
    suffixLength += 1;
  }

  const removedEnd = previous.length - suffixLength;
  const insertedLength = nextCharacters.length - prefixLength - suffixLength;
  const expanded = Array.from({ length: previous.length }, (_, index) => text.characterStyleOverrides[index] ?? 0);
  const inheritedOverride = expanded[Math.max(0, prefixLength - 1)] ?? expanded[prefixLength] ?? 0;
  const nextOverrides = [
    ...expanded.slice(0, prefixLength),
    ...Array.from({ length: insertedLength }, () => inheritedOverride),
    ...expanded.slice(removedEnd),
  ];

  while (nextOverrides.length > 0 && (nextOverrides.at(-1) === 0 || nextOverrides.at(-1) === undefined)) {
    nextOverrides.pop();
  }
  return nextOverrides;
}
